import { SyntheticEvent, useMemo, useState } from 'react';
import Highlighter from 'react-highlight-words';
import InfiniteScroll from 'react-infinite-scroll-component';

import { Avatar, Button, Col, Divider, List, Row } from 'antd';
import { map } from 'lodash';
import { TCustomer } from 'types/customer';

import { CardSkeleton } from '@atoms/skeletons/cardSkeleton';
import { SettingsManager } from '@classes/settingsManager';
import { SkeletonWrapper } from '@common/SkeletonWrapper/SkeletonWrapper';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useFetchDataQuery } from '@hooks/useFetchDataQuery';
import { useSearch } from '@hooks/useSearch';
import { filtersOldToNew, sortsOldToNew } from '@hooks/useSearch/transports';
import { FilterStore } from '@organisms/customersFilter/filterStore';
import { getUserFullName } from '@utils/getUserFullName';
import { imagePicker } from '@utils/imagePicker';
import { formatRussianPhone } from '@utils/russianPhoneInput';

const CUSTOMERS_PAGE_SIZE = 20;

const filterStore = new FilterStore();
const filter = filterStore.filter;

type TProps = {
    /** Клиент, у которого галочка горит выбранной */
    selectedCustomerId?: number | null;
    /** Выбор сделан галочкой; куда вести дальше — решает владелец списка */
    onSelect: (customer: TCustomer) => void;
    /** Запрос из поля поиска в шапке модалки: своего поля у списка нет */
    searchQuery: string;
    /**
     * id контейнера прокрутки. Высоту списку задаёт модалка, поэтому
     * InfiniteScroll слушает этот контейнер, а не собственную высоту в vh.
     */
    listId: string;
    /** Работа владельца поверх списка — например создание профиля клиента */
    isBusy?: boolean;
};

/**
 * Список клиентов CRM с поиском и подгрузкой по прокрутке.
 *
 * Один и тот же список нужен и при создании заказа, и при смене клиента в заказе:
 * выбор там одинаковый — только галочка в карточке, — а дальнейший шаг у модалок свой.
 */
export const CustomerPickerList = ({
    selectedCustomerId,
    onSelect,
    searchQuery,
    listId,
    isBusy = false,
}: TProps) => {
    const [customerList, setCustomerList] = useState<TCustomer[]>([]);

    const newFilters = useMemo(
        () => filtersOldToNew(filter),
        [filter.createdDates, filter.deleted, filter.profile, filter.updatedDates]
    );
    const newSorts = useMemo(() => sortsOldToNew(filter), [filter.sortBy, filter.sortDirection]);

    const {
        isLoading: isListLoading,
        fetchNextPage,
        hasNextPage,
    } = useFetchDataQuery({
        getFnKey: 'getCustomerProfileList',
        params: {
            crm_id: SettingsManager.getConnectionCredentials().crmID,
            per_page: CUSTOMERS_PAGE_SIZE,
        },
        setDataList: setCustomerList,
    });

    const {
        isSearchActive,
        isLoading: isSearchLoading,
        searchResult,
        searchMore,
    } = useSearch({
        model: 'customer-profile',
        query: searchQuery,
        filters: newFilters,
        sorts: newSorts,
        search_attributes: ['name', 'surname', 'phone'],
    });

    const items = isSearchActive ? (searchResult.items as TCustomer[]) : customerList;

    const handleSelectButtonClick = (e: SyntheticEvent, customer: TCustomer) => {
        e.stopPropagation();
        onSelect(customer);
    };

    return (
        <div className="picker-modal__list" id={listId}>
            <SkeletonWrapper
                isLoading={isListLoading || isSearchLoading || isBusy}
                skeleton={[1, 2, 3].map((id) => (
                    <CardSkeleton key={id} />
                ))}
            >
                <InfiniteScroll
                    scrollableTarget={listId}
                    dataLength={items.length}
                    next={isSearchActive ? searchMore : fetchNextPage}
                    hasMore={
                        isSearchActive
                            ? searchResult.items.length < searchResult.pagination.total
                            : hasNextPage
                    }
                    loader={<Divider plain>Загрузка...</Divider>}
                    endMessage={<Divider plain>Все клиенты загружены :^)</Divider>}
                >
                    <List
                        className="customer-card-list"
                        dataSource={items}
                        itemLayout="horizontal"
                        renderItem={(item) => {
                            const avatar =
                                map(item?.pictures, (pic) => imagePicker({ picture: pic }))?.[0] ||
                                imagePicker({ picture: item?.picture });

                            return (
                                <List.Item className="customer-card" key={item.id}>
                                    <Row style={{ width: '100%' }}>
                                        <Col span={3}>
                                            <Avatar className="avatar" src={avatar} />
                                        </Col>
                                        <Col span={18}>
                                            <h3 className="title">
                                                <Highlighter
                                                    className="ant-typography"
                                                    searchWords={searchQuery.split(' ') || []}
                                                    autoEscape
                                                    textToHighlight={getUserFullName(
                                                        item,
                                                        'Без имени'
                                                    )}
                                                />
                                            </h3>
                                            {/* у профиля клиента номер бывает пустым — тогда он у пользователя */}
                                            <div className="phone">
                                                {formatRussianPhone(item?.phone || item?.user?.phone)}
                                            </div>
                                        </Col>
                                        <Col span={3} className="button-select">
                                            <Button
                                                icon={<FontAwesomeIcon icon={faCheck} />}
                                                shape="circle"
                                                type={
                                                    Number(item?.id) === Number(selectedCustomerId)
                                                        ? 'primary'
                                                        : 'default'
                                                }
                                                onClick={(e) => handleSelectButtonClick(e, item)}
                                            />
                                        </Col>
                                    </Row>
                                </List.Item>
                            );
                        }}
                    />
                </InfiniteScroll>
            </SkeletonWrapper>
        </div>
    );
};
